import { useOnboardingPendingReplay } from "@/hooks/useOnboardingPendingReplay";
import { useEffect, lazy, Suspense } from "react";
import posthog from "posthog-js";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { BadgeCelebrationProvider } from "@/components/badges";
import { StreakCelebrationProvider } from "@/components/streak";
import { FeedbackPromptProvider } from "@/components/FeedbackPromptProvider";
import { AppReviewPromptProvider } from "@/components/AppReviewPromptProvider";
import { PostVisitReflectionProvider } from "@/components/PostVisitReflectionProvider";
import { IncompleteVibePromptProvider } from "@/components/IncompleteVibePromptProvider";
import { BottomPromptSlotProvider } from "@/components/BottomPromptSlot";
import { WorkTimerProvider } from "@/components/timer";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RequireAuth } from "@/components/RequireAuth";
import { SyncProgressProvider } from "@/components/admin/SyncProgressProvider";
import { PromptQueueProvider } from "@/contexts/PromptQueueContext";
import { WifiSpeedTestProvider } from "@/contexts/WifiSpeedTestContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useSessionTracking } from "@/hooks/useSessionTracking";
import { useAuth } from "@/hooks/useAuth";
import {
  clearStoredPushTarget,
  getPushTargetFromHash,
  getPushTargetFromSearch,
  getStoredPushTarget,
  storePushTarget,
  stripPushIdFromAddress,
} from "@/lib/pushNavigation";
import { useNotificationEventListener } from "@/lib/notifListener";
import { InAppHistoryTracker } from "@/lib/inAppHistory";

import { UsernamePromptDialog } from "@/components/UsernamePromptDialog";
import { NamePromptDialog } from "@/components/NamePromptDialog";
import { ProximityCheckInPrompt } from "@/components/ProximityCheckInPrompt";
import { FollowupVibePromptProvider } from "@/components/FollowupVibePromptProvider";
import { StayTrackingProvider } from "@/components/StayTrackingProvider";
import { LocationPromptDialog } from "@/components/LocationPromptDialog";
import { TermsAcceptanceDialog } from "@/components/TermsAcceptanceDialog";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
import { ReferralCapture } from "@/components/ReferralCapture";
import { ReferralDebugBanner } from "@/components/ReferralDebugBanner";
import { ActivityBanner } from "@/components/ActivityBanner";
import { SaveConfirmation } from "@/components/SaveConfirmation";
import PrerenderReady from "./components/PrerenderReady";
import { MascotLoader } from "./components/MascotLoader";
import { AppRouteTiming } from "@/components/AppRouteTiming";

// Eager: routes that are prerendered at build time (the crawler snapshots on
// first paint, before a lazy chunk could load), plus auth and the 404 catch-all.
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import Launch from "./pages/Launch";
import Support from "./pages/Support";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import BestCafesPage from "./pages/BestCafesPage";

// Lazy: everything else loads in its own chunk on first navigation, so the
// initial bundle no longer carries the map, admin, and owner dashboards.
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Browse = lazy(() => import("./pages/Browse"));
const Feed = lazy(() => import("./pages/Feed"));
const CafeDetail = lazy(() => import("./pages/CafeDetail"));
const Admin = lazy(() => import("./pages/Admin"));
const NativeCallback = lazy(() => import("./pages/NativeCallback"));
const Favorites = lazy(() => import("./pages/Favorites"));
const Profile = lazy(() => import("./pages/Profile"));
const MyPhotos = lazy(() => import("./pages/MyPhotos"));
const PhotoShare = lazy(() => import("./pages/PhotoShare"));
const SharedList = lazy(() => import("./pages/SharedList"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const OwnerDashboard = lazy(() => import("./pages/OwnerDashboard"));
const OwnerScore = lazy(() => import("./pages/OwnerScore"));
const OwnerNotifications = lazy(() => import("./pages/OwnerNotifications"));
const OwnerMessages = lazy(() => import("./pages/OwnerMessages"));
const OwnerLogin = lazy(() => import("./pages/OwnerLogin"));
const OwnerInvite = lazy(() => import("./pages/OwnerInvite"));
const OwnerReviews = lazy(() => import("./pages/OwnerReviews"));
const OwnerMedia = lazy(() => import("./pages/OwnerMedia"));
const OwnerListing = lazy(() => import("./pages/OwnerListing"));
const OwnerAnnouncements = lazy(() => import("./pages/OwnerAnnouncements"));
const OwnerShare = lazy(() => import("./pages/OwnerShare"));
const OwnerSettings = lazy(() => import("./pages/OwnerSettings"));

const BestCafesAdmin = lazy(() => import("./pages/BestCafesAdmin"));
const WorkCafesCity = lazy(() => import("./pages/WorkCafesCity"));
const WorkCafesAdmin = lazy(() => import("./pages/WorkCafesAdmin"));
const DiscoverPeople = lazy(() => import("./pages/DiscoverPeople"));
// Private utility: not linked, not in the sitemap, not prerendered, noindexed.
const OptOut = lazy(() => import("./pages/OptOut"));
const ClaimConfirm = lazy(() => import("./pages/ClaimConfirm"));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes - prevents unnecessary refetches
      gcTime: 1000 * 60 * 10,   // 10 minutes cache
      retry: 2,
      refetchOnMount: true,     // Only refetch if stale
      refetchOnWindowFocus: false, // Don't refetch on every tab switch
    },
  },
});

// Prefetch cafe data early so Browse page loads instantly
// Only fetches the top 30 cafes (sorted by score) for fast initial render

function PushRegistrar() {
  usePushNotifications();
  return null;
}

function OnboardingPendingReplay() {
  useOnboardingPendingReplay();
  return null;
}

function SessionTracker() {
  const { user } = useAuth();
  useSessionTracking(user?.id ?? null);
  return null;
}

/**
 * Analytics only: fires `push_opened` once per app load when the OneSignal
 * `onesignal_push_id` parameter is present on the launch URL. Module-level flag
 * so route changes and re-renders can never double-fire.
 */
let hasCapturedPushOpen = false;

function capturePushOpenOnce(target: string | null) {
  if (hasCapturedPushOpen) return;
  let pushId: string | null = null;
  try {
    pushId =
      new URLSearchParams(window.location.search).get("onesignal_push_id") ||
      new URLSearchParams((window.location.hash || "").replace(/^#/, "")).get("onesignal_push_id");
  } catch {
    pushId = null;
  }
  if (!pushId) return;
  hasCapturedPushOpen = true;
  try {
    posthog.capture("push_opened", { push_id: pushId, notif_path: target ?? null });
  } catch { /* ignore */ }
  // Remove the id from the address on every path, including the one where the
  // push carried no destination, so a later load cannot count the same open again.
  stripPushIdFromAddress();
}

/**
 * Handles deep-link navigation from push notifications. Native app launches can
 * drop the original path, so we read it from hash/query and persist it until
 * the app is ready to navigate.
 */
function NotifPathHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  useNotificationEventListener(navigate, location);


  useEffect(() => {
    const hashTarget = getPushTargetFromHash(window.location.hash || "");
    const searchTarget = getPushTargetFromSearch(location.search);
    const storedTarget = getStoredPushTarget();
    const target = hashTarget || searchTarget || storedTarget;

    // Capture before any URL cleanup below strips the push id.
    capturePushOpenOnce(target);

    if (!target) return;

    if (hashTarget || searchTarget) {
      storePushTarget(target);
    }

    if (location.pathname === "/auth" || location.pathname === "/owner-login") {
      // Read the live search: the router's location does not observe the
      // replaceState above, so reusing it would write the push id back.
      const liveSearch = window.location.search || "";
      if (window.location.hash || liveSearch.includes("notif_path=")) {
        window.history.replaceState(null, "", location.pathname + liveSearch.replace(/([?&])notif_path=[^&]*&?/, "$1").replace(/[?&]$/, ""));
      }
      return;
    }

    if (location.pathname === target) {
      clearStoredPushTarget();
      return;
    }

    if (window.location.hash) {
      // Live search for the same reason as the /auth branch above.
      window.history.replaceState(null, "", location.pathname + (window.location.search || ""));
    }

    clearStoredPushTarget();
    navigate(target, { replace: true });
  }, [location.pathname, location.search, navigate]);

  return null;
}

// The list-cafes prefetch was removed: it fetched anonymously (no
// Authorization header) into query key ["cafes"], which nothing reads —
// useBrowseCafes reads an auth-scoped key. Re-pointing it at the real key
// would race an anonymous response into a signed-in session, which is exactly
// the bug the auth-aware cache identity fixes. Browse's own query already
// fires on mount, so there is nothing to preserve.
function CafePrefetcher() {
  return null;
}



const App = () => (
  <ErrorBoundary>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <CafePrefetcher />
        <ReferralCapture />
        <ReferralDebugBanner />
        <PushRegistrar />
        <OnboardingPendingReplay />
        <SessionTracker />
        <InAppHistoryTracker />
        <NotifPathHandler />
        <AppRouteTiming />
        <PrerenderReady />

        <TooltipProvider>
          <PromptQueueProvider>
          <BadgeCelebrationProvider>
          <StreakCelebrationProvider>
            <FeedbackPromptProvider>
              <AppReviewPromptProvider>
              <PostVisitReflectionProvider>
              <WifiSpeedTestProvider>
              <IncompleteVibePromptProvider>
                <BottomPromptSlotProvider>
                <WorkTimerProvider>
                <SyncProgressProvider>

                  <Toaster />
                  <Sonner />
                  <ActivityBanner />
                  <SaveConfirmation />

                  <NamePromptDialog />
                  <UsernamePromptDialog />
                  
                  <ProximityCheckInPrompt />
                  <FollowupVibePromptProvider />
                  <StayTrackingProvider />
                  <LocationPromptDialog />
                  <TermsAcceptanceDialog />

                  <Suspense fallback={<div className="flex min-h-[60vh] items-center justify-center"><MascotLoader /></div>}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/browse" element={<Browse />} />
                    <Route path="/feed" element={<RequireAuth><Feed /></RequireAuth>} />
                    <Route path="/cafe/:slug" element={<CafeDetail />} />
                    <Route path="/p/:photoId" element={<PhotoShare />} />
                    <Route path="/list/:token" element={<SharedList />} />

                    <Route path="/admin" element={<Admin />} />
                    <Route path="/favorites" element={<RequireAuth><Favorites /></RequireAuth>} />
                    <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
                    <Route path="/my-photos" element={<RequireAuth><MyPhotos /></RequireAuth>} />
                    <Route path="/user/:userId" element={<RequireAuth><PublicProfile /></RequireAuth>} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/native-callback" element={<NativeCallback />} />
                    <Route path="/unsubscribe" element={<Unsubscribe />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="/launch" element={<Launch />} />
                  <Route path="/owner-login" element={<OwnerLogin />} />
                  <Route path="/invite/:token" element={<OwnerInvite />} />
                    <Route path="/owner" element={<OwnerDashboard />} />
                    <Route path="/owner/score" element={<OwnerScore />} />
                    <Route path="/owner/notifications" element={<OwnerNotifications />} />
                    <Route path="/owner/messages" element={<OwnerMessages />} />
                    <Route path="/owner/reviews" element={<OwnerReviews />} />
                    <Route path="/owner/media" element={<OwnerMedia />} />
                    <Route path="/owner/listing" element={<OwnerListing />} />
                    <Route path="/owner/announcements" element={<OwnerAnnouncements />} />
                    <Route path="/owner/share" element={<OwnerShare />} />
                    <Route path="/owner/settings" element={<OwnerSettings />} />
                    
                    <Route path="/best-cafes/:neighborhood" element={<BestCafesPage />} />
                    <Route path="/best-cafes-admin" element={<BestCafesAdmin />} />
                    <Route path="/work-cafes/:city" element={<WorkCafesCity />} />
                    <Route path="/work-cafes-admin" element={<WorkCafesAdmin />} />

                    <Route path="/discover-people" element={<RequireAuth><DiscoverPeople /></RequireAuth>} />
                    <Route path="/optout" element={<OptOut />} />
                    <Route path="/claim/confirm" element={<ClaimConfirm />} />

                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </Suspense>
                  <MobileBottomNav />

                </SyncProgressProvider>
                </WorkTimerProvider>
                </BottomPromptSlotProvider>
                </IncompleteVibePromptProvider>
                </WifiSpeedTestProvider>

              </PostVisitReflectionProvider>
              </AppReviewPromptProvider>
            </FeedbackPromptProvider>
          </StreakCelebrationProvider>
          </BadgeCelebrationProvider>
          </PromptQueueProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </ErrorBoundary>
);

export default App;